import { useCallback, useRef, useState } from 'react';
import downloadApi from '../utils/api/downloadApi';

/**
 * Shared download orchestration for the royalty and title statement tables:
 * tracks which file is in flight and writes the blob via {@link downloadApi}.
 *
 * Axios failures from {@link downloadApi} helpers surface via the axios
 * interceptor and global ErrorModal. Pre-HTTP downloadApi failures (e.g.
 * missing redirect URL) are reported there too before reject. This catch only
 * prevents an unhandled rejection after feedback is already shown.
 *
 * @param fetchBlob SDK helper with `(fileName, entityId)` argument order
 */
export const useRoyaltyFileDownload = <TFileName extends string>(
  fetchBlob: (fileName: TFileName, entityId: string) => Promise<Blob>,
) => {
  const [downloadingKeys, setDownloadingKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const downloadingKeysRef = useRef(new Set<string>());

  const handleDownload = useCallback(
    async (entityId: string, fileName: TFileName, savedFileName: string) => {
      const key = `${entityId}:${fileName}`;

      if (downloadingKeysRef.current.has(key)) {
        return;
      }
      downloadingKeysRef.current.add(key);
      setDownloadingKeys(new Set(downloadingKeysRef.current));

      try {
        const blob = await fetchBlob(fileName, entityId);

        downloadApi.downloadFile(blob, savedFileName);
      } catch {
        // ErrorModal already shown (axios interceptor or downloadApi pre-HTTP check).
      } finally {
        downloadingKeysRef.current.delete(key);
        setDownloadingKeys(new Set(downloadingKeysRef.current));
      }
    },
    [fetchBlob],
  );

  const isDownloading = useCallback(
    (entityId: string, fileName: TFileName) =>
      downloadingKeys.has(`${entityId}:${fileName}`),
    [downloadingKeys],
  );

  return { handleDownload, isDownloading };
};

export type RoyaltyFileDownload<TFileName extends string> = ReturnType<
  typeof useRoyaltyFileDownload<TFileName>
>;
